'use client';

import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renderToString } from 'react-dom/server';

import Image from 'next/image';
import Link from 'next/link';

import { SearchBarContext } from '@/app/providers/SearchBarProvider';
import { Routes } from '@/app/enums';
import { User } from '@/app/api';

import donioLogo from '../../../public/public/images/logo/donio-logo.svg';

import MainNavigation from '@/app/components/MainNavigation/MainNavigation';
import Button, { ButtonHTMLTag, ButtonSize } from '@/app/components/Button/Button';
import LensIcon from '@/app/components/LensIcon/LensIcon';
import Search from '@/app/components/Search/Search';
import Avatar, { AvatarSize, AvatarStyle } from '@/app/components/Avatar/Avatar';
import Tooltip from '@/app/components/Tooltip/Tooltip';
import ProfileNavigation from '@/app/components/ProfileNavigation/ProfileNavigation';
import DrawerProfileNavigation from '@/app/components/DrawerProfileNavigation/DrawerProfileNavigation';
import classNames from 'classnames';
import { PropsWithClassname } from '@/app/types';

interface HeaderProps extends PropsWithClassname {
  currentUser?: User;
}

const Header = ({ currentUser, className }: HeaderProps) => {
  const { t } = useTranslation();

  const { isSearchBarOpen, setIsSearchBarOpen } = useContext(SearchBarContext);
  const [isProfileNavigationDrawerOpen, setIsProfileNavigationDrawerOpen] = useState(false);

  const handleOpenSearchBar = useCallback(() => {
    setIsSearchBarOpen(true);
  }, [setIsSearchBarOpen]);

  const handleOpenProfileNavigationDrawer = useCallback(() => {
    setIsProfileNavigationDrawerOpen(true);
  }, [setIsProfileNavigationDrawerOpen]);

  const handleCloseProfileNavigationDrawer = useCallback(() => {
    setIsProfileNavigationDrawerOpen(false);
  }, [setIsProfileNavigationDrawerOpen]);

  const handleCloseSearchBar = useCallback(() => {
    setIsSearchBarOpen(false);
  }, [setIsSearchBarOpen]);

  return (
    <header className={classNames('w-full default-page-max-width px-[24px] mx-auto flex', className)}>
      <a href={process.env.NEXT_PUBLIC_REDIRECT_URL} className="flex items-center shrink-0 order-1">
        <Image src={donioLogo} alt="Donio logo" className="w-auto h-[34px] md:h-[42px] relative z-40" />
      </a>
      <div className="flex justify-end order-3 xl:order-2 xl:grow">
        <MainNavigation currentUser={currentUser} />
      </div>
      <div className="flex items-center order-2 xl:order-3 grow xl:grow-0 justify-end">
        <ul className="flex">
          <li
            className="ml-[24px] flex items-center"
            data-tooltip-id={!!currentUser ? 'profile-menu' : undefined}
            data-tooltip-html={
              !!currentUser ? renderToString(<ProfileNavigation currentUser={currentUser} />) : undefined
            }
          >
            {!!currentUser && (
              <Avatar
                size={AvatarSize.ExtraSmall}
                style={AvatarStyle.Primary}
                placeholderText={currentUser.firstName || currentUser.fullName || currentUser.email}
                className="cursor-pointer"
                onClick={handleOpenProfileNavigationDrawer}
              />
            )}
            {!currentUser && (
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                className="flex hover:text-primary-mid"
                href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.LOGIN}`}
              >
                {t('components.Header.signIn')}
              </Link>
            )}
          </li>
          <li className="flex">
            <button
              onClick={handleOpenSearchBar}
              className="hover:text-primary-mid cursor-pointer mx-[16px] xl:mx-[24px]"
            >
              <LensIcon />
            </button>
          </li>
          <li className="hidden xl:flex items-center">
            <Button
              size={ButtonSize.MEDIUM}
              tagSpecificProps={{
                htmlTag: ButtonHTMLTag.LINK,
                href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.SUGGESTION}`,
              }}
            >
              {t('components.Header.startBrowsing')}
            </Button>
          </li>
        </ul>
      </div>
      <Search isOpen={isSearchBarOpen} onClose={handleCloseSearchBar} />
      <Tooltip id="profile-menu" place="bottom" className="hidden xl:block !min-w-[336px] !py-[24px] shadow-sm" />
      {!!currentUser && (
        <DrawerProfileNavigation
          currentUser={currentUser}
          isOpen={isProfileNavigationDrawerOpen}
          onClose={handleCloseProfileNavigationDrawer}
        />
      )}
    </header>
  );
};

export default Header;
